import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useBatch } from '@/hooks/useDispatcher';
import { formatDateTime } from '@/lib/utils';

export default function BatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const batch = useBatch(id);

  return (
    <div>
      <Link
        to="/batches"
        className="text-xs text-gray-500 hover:text-brand-dark inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft size={12} /> Retour aux batches
      </Link>
      <PageHeader
        title={batch.data?.campaign_name ?? batch.data?.source_batch_id ?? '…'}
        description={batch.data ? `Source: ${batch.data.source}` : undefined}
        action={batch.data ? <StatusBadge status={batch.data.status} /> : null}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Workspace" value={batch.data?.workspace_id ?? '—'} />
        <Stat label="Jobs" value={String(batch.data?.jobs_count ?? '—')} />
        <Stat label="Contacts" value={String(batch.data?.contacts_count ?? '—')} />
        <Stat
          label="Cache"
          value={batch.data?.cache_hit === undefined ? '—' : batch.data.cache_hit ? 'hit' : 'miss'}
        />
      </div>

      <Card className="mb-6">
        <CardHeader title="Configuration" />
        <div className="px-5 py-4 space-y-2 text-sm">
          <Row label="Channels">
            <div className="flex gap-1">
              {(batch.data?.channels_enabled ?? []).map((c) => (
                <Badge key={c} tone="brand">
                  {c}
                </Badge>
              ))}
            </div>
          </Row>
          <Row label="Callback URL">
            <span className="font-mono text-xs text-gray-600 break-all">
              {batch.data?.callback_url ?? '—'}
            </span>
          </Row>
          <Row label="Créé">
            <span>{formatDateTime(batch.data?.created_at)}</span>
          </Row>
        </div>
      </Card>

      <Card>
        <CardHeader title="Jobs du batch" subtitle={`${batch.data?.jobs?.length ?? 0} entrées`} />
        {batch.isLoading ? (
          <div className="p-5">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !batch.data?.jobs?.length ? (
          <EmptyState title="Aucun job dans ce batch" />
        ) : (
          <div className="divide-y divide-brand-border">
            {batch.data.jobs.map((j) => (
              <Link
                key={j.id}
                to={`/jobs/${j.id}`}
                className="flex items-center justify-between px-5 py-3 table-row-hover"
              >
                <div>
                  <div className="text-sm font-medium text-brand-dark">{j.industry}</div>
                  <div className="text-xs text-gray-400">
                    {j.location_query} · VM {j.vm_slug ?? '—'}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">{j.contacts_count ?? 0} contacts</span>
                  <StatusBadge status={j.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="font-heading text-brand-dark text-base mt-1 truncate">{value}</div>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <span className="w-32 shrink-0 text-xs text-gray-400">{label}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}
